import React, { FC, useState, useEffect, useId } from 'react';
import { speechManager } from '../lib/speech';

export interface UseSpeechResult {
  isPlaying: boolean;
  isLoading: boolean;
  speak: (text: string, lang?: string) => void;
  stop: () => void;
  toggle: (text: string, lang?: string) => void;
}

export const useSpeech = (customId?: string): UseSpeechResult => {
  const autoId = useId();
  const id = customId || autoId;
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const unsubscribe = speechManager.subscribe((activeId, state) => {
      if (activeId === id) {
        setIsPlaying(state === 'playing');
        setIsLoading(state === 'loading');
      } else {
        setIsPlaying(false);
        setIsLoading(false);
      }
    });

    return () => {
      unsubscribe();
      // If this item was playing when unmounting, stop it cleanly
      if (speechManager.getActiveId() === id) {
        speechManager.stop();
      }
    };
  }, [id]);

  const speak = (text: string, lang?: string) => {
    speechManager.speak(id, text, lang);
  };

  const stop = () => {
    if (speechManager.getActiveId() === id) {
      speechManager.stop();
    }
  };

  const toggle = (text: string, lang?: string) => {
    if (isPlaying || isLoading) {
      stop();
    } else {
      speak(text, lang);
    }
  };

  return { isPlaying, isLoading, speak, stop, toggle };
};

export interface ListenButtonProps {
  id: string;
  text: string;
  language?: string;
  variant?: 'bubble-own' | 'bubble-other' | 'announcement' | 'kudo' | 'pill' | 'inline' | 'ghost';
  label?: string;
  stopLabel?: string;
  size?: 'xs' | 'sm' | 'md';
  className?: string;
  showLabel?: boolean;
}

export const ListenButton: FC<ListenButtonProps> = ({
  id,
  text,
  language = 'en',
  variant = 'inline',
  label = 'Listen',
  stopLabel = 'Stop',
  size = 'xs',
  className = '',
  showLabel = true,
}) => {
  const { isPlaying, isLoading, toggle } = useSpeech(id);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggle(text, language);
  };

  if (!text || text.trim().length === 0) {
    return null;
  }

  // Base styling depending on variant
  let styleClasses = '';
  const iconSize = size === 'xs' ? 'text-[13px]' : size === 'sm' ? 'text-[15px]' : 'text-base';
  const textSize = size === 'xs' ? 'text-xs' : size === 'sm' ? 'text-xs' : 'text-sm';

  switch (variant) {
    case 'bubble-own':
      styleClasses = isPlaying
        ? 'bg-white/25 text-white ring-1 ring-white/50'
        : 'text-indigo-200 hover:text-white hover:bg-white/10';
      break;

    case 'bubble-other':
      styleClasses = isPlaying
        ? 'bg-indigo-600 text-white shadow-sm'
        : 'text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50';
      break;

    case 'announcement':
      styleClasses = isPlaying
        ? 'bg-amber-600 text-white shadow-sm'
        : 'text-amber-800 hover:text-amber-950 hover:bg-amber-100/80';
      break;

    case 'kudo':
      styleClasses = isPlaying
        ? 'bg-amber-600 text-white shadow-sm'
        : 'text-amber-800 hover:text-amber-950 hover:bg-amber-100/80';
      break;

    case 'pill':
      styleClasses = isPlaying
        ? 'bg-indigo-600 text-white shadow-sm ring-1 ring-indigo-500'
        : 'text-indigo-600 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200';
      break;

    case 'inline':
    default:
      styleClasses = isPlaying
        ? 'bg-indigo-100 text-indigo-800 font-bold'
        : 'text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50/80';
      break;
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isLoading}
      title={isPlaying ? "Stop listening" : "Listen to audio read aloud"}
      aria-label={isPlaying ? stopLabel : label}
      className={`inline-flex items-center gap-1 font-semibold px-2 py-0.5 rounded-md transition-all duration-150 select-none ${styleClasses} ${className}`}
    >
      {isLoading ? (
        <span className={`material-icons-round ${iconSize} animate-spin`}>progress_activity</span>
      ) : isPlaying ? (
        <span className={`material-icons-round ${iconSize} animate-pulse text-current`}>stop</span>
      ) : (
        <span className={`material-icons-round ${iconSize}`}>volume_up</span>
      )}

      {showLabel && (
        <span className={`${textSize} leading-none`}>
          {isLoading ? 'Loading...' : isPlaying ? stopLabel : label}
        </span>
      )}
    </button>
  );
};
